import { useEffect, useState, FormEvent, useContext } from "react";
import { getTokenForWeb, getCustomerDetailsByToken, liveChatAgentLogin } from "../services/auth";
import { checkLiveChatAccess } from "../services/checkRole";
import { decrypt, encrypt, getOrCreateDeviceId, validUserLogin } from "../utils/Util";
import { IUserLogin } from "../models/IUserLogin";
import { ILoginInfo } from "../models/ILoginInfo";
import { Context } from "../utils/AppContext";
import { useNavigate, Link } from "react-router-dom";

function LiveChatLogin() {
  const navigate = useNavigate();
  const context = useContext(Context);

  const hasSaved = !!(localStorage.lcUsername && localStorage.lcPassword);
  const [remember, setRemember] = useState(hasSaved);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const [userdata, setUserdata] = useState<IUserLogin>({
    email: localStorage.lcUsername ? decrypt(localStorage.lcUsername) : "",
    password: localStorage.lcPassword ? decrypt(localStorage.lcPassword) : "",
    deviceId: getOrCreateDeviceId(),
  });

  useEffect(() => {
    // Web token al
    getTokenForWeb()
      .then((res) => {
        if (res?.data) {
          localStorage.setItem("MA", encrypt(JSON.stringify(res.data)));
        }
      })
      .catch((err) => {
        console.log("error", err.message);
      });
  }, []);

  const handleLogin = (evt: FormEvent) => {
    evt.preventDefault();
    const status = validUserLogin(userdata.email, userdata.password);
    if (!status) return;

    if (remember) {
      localStorage.setItem("lcUsername", encrypt(userdata.email));
      localStorage.setItem("lcPassword", encrypt(userdata.password));
    } else {
      localStorage.removeItem("lcUsername");
      localStorage.removeItem("lcPassword");
    }

    setErrorMessage("");
    setLoading(true);
    liveChatAgentLogin(userdata)
      .then(async (res) => {
        const tokenData = res.data;
        // Rol kontrolü için önce token'ı geçici olarak sakla (yetkili istekler için gerekli).
        const provisional: ILoginInfo = { ...tokenData, restourantId: "" };
        localStorage.setItem("user", encrypt(JSON.stringify(provisional)));

        let access;
        try {
          access = await checkLiveChatAccess();
        } catch {
          localStorage.removeItem("user");
          setErrorMessage("Yetki bilgileri alınamadı. Lütfen tekrar deneyin.");
          setLoading(false);
          return;
        }

        if (!access.isSuperAdmin && !access.isLiveChatAgent) {
          localStorage.removeItem("user");
          setErrorMessage("Bu hesabın LiveChat paneline erişim yetkisi yok.");
          setLoading(false);
          return;
        }

        const finalInfo: ILoginInfo = {
          ...tokenData,
          restourantId: "",
          isLiveChatOnly: !access.isSuperAdmin,
        };
        localStorage.setItem("user", encrypt(JSON.stringify(finalInfo)));

        try {
          const detailsRes = await getCustomerDetailsByToken();
          const merged = { ...finalInfo, ...detailsRes.data, isLiveChatOnly: finalInfo.isLiveChatOnly };
          localStorage.setItem("user", encrypt(JSON.stringify(merged)));
          context.setName(merged as any);
        } catch {
          context.setName(finalInfo as any);
        }

        // Bu giriş ekranından gelen herkes doğrudan LiveChat ekranına yönlendirilir;
        // SuperAdmin'ler ek olarak navbar/tüm menülere de erişebilir (bkz. Control.tsx).
        navigate("/liveChat", { replace: true });
      })
      .catch((err) => {
        localStorage.removeItem("user");
        const data = err?.response?.data;
        const apiMsg = Array.isArray(data) && data[0]?.description
          ? data[0].description
          : err.message;
        setErrorMessage(apiMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #eef2ff 0%, #fafafa 60%, #f5f3ff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#fff',
          borderRadius: '20px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
          padding: '44px 40px 36px',
          boxSizing: 'border-box',
        }}
      >
        {/* Logo / Başlık */}
        <div className="text-center mb-4">
          <div
            style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              borderRadius: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
            }}
          >
            <i className="bi bi-headset text-white" style={{ fontSize: '1.6rem' }}></i>
          </div>
          <h4 className="fw-bold mb-1" style={{ color: '#111827', letterSpacing: '-0.3px' }}>
            LiveChat Temsilci Girişi
          </h4>
          <p className="text-muted small mb-0">Canlı destek panelinize giriş yapın</p>
        </div>

        {/* Hata mesajı */}
        {errorMessage && (
          <div
            className="d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-3"
            style={{ background: '#fef2f2', border: '1.5px solid #fecaca', color: '#b91c1c', fontSize: '0.85rem' }}
          >
            <i className="bi bi-exclamation-circle-fill flex-shrink-0"></i>
            <span>{errorMessage}</span>
            <button
              onClick={() => setErrorMessage("")}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', padding: 0 }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin}>
          {/* Email */}
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: '#374151' }}>
              E-posta
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRight: 'none', borderRadius: '10px 0 0 10px', color: '#9ca3af' }}
              >
                <i className="bi bi-envelope-fill"></i>
              </span>
              <input
                type="email"
                value={userdata.email}
                onChange={(e) => setUserdata({ ...userdata, email: e.target.value })}
                className="form-control"
                placeholder="ornek@email.com"
                style={{
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRadius: '0 10px 10px 0',
                  fontSize: '0.93rem',
                  padding: '10px 14px',
                  boxShadow: 'none',
                }}
                required
              />
            </div>
          </div>

          {/* Şifre */}
          <div className="mb-3">
            <label className="form-label small fw-semibold mb-1" style={{ color: '#374151' }}>
              Şifre
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRight: 'none', borderRadius: '10px 0 0 10px', color: '#9ca3af' }}
              >
                <i className="bi bi-lock-fill"></i>
              </span>
              <input
                type={showPass ? "text" : "password"}
                value={userdata.password}
                onChange={(e) => setUserdata({ ...userdata, password: e.target.value })}
                className="form-control"
                placeholder="••••••••"
                style={{
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRight: 'none',
                  fontSize: '0.93rem',
                  padding: '10px 14px',
                  boxShadow: 'none',
                }}
                required
              />
              <button
                type="button"
                className="input-group-text"
                onClick={() => setShowPass(!showPass)}
                style={{
                  background: '#f9fafb',
                  border: '1.5px solid #e5e7eb',
                  borderLeft: 'none',
                  borderRadius: '0 10px 10px 0',
                  color: '#9ca3af',
                  cursor: 'pointer',
                }}
              >
                <i className={`bi ${showPass ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
              </button>
            </div>
          </div>

          {/* Beni Hatırla */}
          <div className="d-flex align-items-center mb-4">
            <div
              onClick={() => setRemember(!remember)}
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '5px',
                border: `2px solid ${remember ? '#6366f1' : '#d1d5db'}`,
                background: remember ? '#6366f1' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s ease',
              }}
            >
              {remember && <i className="bi bi-check text-white" style={{ fontSize: '0.7rem', lineHeight: 1 }}></i>}
            </div>
            <label
              onClick={() => setRemember(!remember)}
              className="ms-2 small"
              style={{ color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}
            >
              Beni hatırla
            </label>
          </div>

          {/* Giriş Butonu */}
          <button
            type="submit"
            className="btn w-100"
            disabled={loading}
            style={{
              background: loading ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.97rem',
              borderRadius: '10px',
              padding: '11px',
              border: 'none',
              boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              transition: 'all 0.2s ease',
              letterSpacing: '0.2px',
            }}
          >
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Giriş yapılıyor...</>
            ) : (
              <><i className="bi bi-box-arrow-in-right me-2"></i>Giriş Yap</>
            )}
          </button>
        </form>

        {/* Yönetici girişine dön */}
        <div className="text-center" style={{ marginTop: '20px' }}>
          <Link
            to="/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: '#6b7280', fontWeight: 600, fontSize: '0.85rem',
              textDecoration: 'none',
            }}
          >
            <i className="bi bi-arrow-left"></i> Yönetici girişine dön
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LiveChatLogin;
